/**
 * 69.4, the forward base (FOB; DIRECTION §0.8 69.4, the design note in BUILDLIST 69.4, decision 163(j)), with
 * GameConfig.fobs (and relays: the web it needs). A FOB is a structure three existing systems read:
 * - **a node of the side's web** (core/web.ts): it links within the relay's link range, so it extends the web and needs it;
 * - **a team crate** (`supply`, core/supply.ts): its own bandages and magazines (FOB.stock), handed out as any crate's —
 *   linked or cut (a cut FOB keeps handing out what it holds: a slow starvation; an emptied crate is skipped as any is);
 * - **where the side's pooled stocks are handed out forward** (65.23's grenades, 65.29's rockets): a team
 *   crate in reach tops a unit up from its side's pool — at a FOB only while it is linked (`servesPool`).
 * Linked, its crate refills from the base over time (`refillFobCrates`, a bandage and a magazine every FOB.refillS up to
 * its stock) and it sees as a relay does (core/relay.ts). Cut, it refills nothing, draws nothing from the pools and sees
 * nothing.
 *
 * Built on the build path (core/build.ts): the side's count (`state.fobsLeft`, FOB.perSide), held still FOB.plantSeconds,
 * not in the sector in play under Operations (69.6f), and **only where the web would link it** (`fobLinkRefusal`: the
 * web's own rule asked of the FOB as if it stood there). Pure; nothing here runs without the switch.
 */
import type { GameState, GameConfig, StructureState, Vec3 } from '../types';
import { FOB, MUNITIONS } from '../constants';
import { isLinked, linkedIds } from './web';
import { structureHealth, structureTeam } from './body';
import { linkRefills } from './convoys';
import { vecNormalize } from '../math';

/** Are FOBs on in this match (they need the web: relays on)? */
export function fobsOn(config: Pick<GameConfig, 'fobs' | 'relays'>): boolean {
  return !!config.fobs && !!config.relays;
}

/** The setup's count: every side FOB.perSide to plant. */
export function withFobs(state: GameState): GameState {
  if (!fobsOn(state.config)) return state;
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const fobsLeft: Record<number, number> = {};
  for (const t of teams) fobsLeft[t] = FOB.perSide;
  return { ...state, fobsLeft };
}

/** The forward bases `team` may still plant. */
export function fobsLeftFor(state: Pick<GameState, 'fobsLeft'>, team: number): number {
  return state.fobsLeft?.[team] ?? 0;
}

/** A new FOB's own crate: the side's, full (69.30 step 1: with the munitions supply on, the tank's rounds too — MUNITIONS.fob, fixed). */
export function fobSupply(team: number, config?: Pick<GameConfig, 'munitionsSupply'> & Partial<Pick<GameConfig, 'finiteSupply'>>): NonNullable<StructureState['supply']> {
  const bare = !!config?.finiteSupply;   // (DECISIONS 402: with a finite stock it starts bare, filled out of the containers by its refills and convoys)
  return { teamIndex: team, bandages: bare ? 0 : FOB.stock.bandages, magazines: bare ? 0 : FOB.stock.magazines, ...(config?.munitionsSupply ? { ammo: { ...MUNITIONS.fob } } : {}) };
}

/**
 * Would a FOB planted at `at` by `team` be linked? The web's own rule (the sparse web, 162) asked of it as if it stood
 * there now: null when it would, else why not. A FOB planted stranded would be a crate cut from the start.
 */
export function fobLinkRefusal(state: GameState, team: number, at: { planetIndex: number; surfaceDir: Vec3 }): string | null {
  const owner = state.players.find(p => p.teamIndex === team);
  if (!owner) return 'No side to link it to';
  const id = state.structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
  const probe: StructureState = { id, type: 'fob', planetIndex: at.planetIndex, surfaceDir: vecNormalize(at.surfaceDir), health: structureHealth('fob'), ownerId: owner.id };
  return linkedIds({ ...state, structures: [...state.structures, probe] }, team).includes(id) ? null : 'Only where the web links it: within link range of a linked relay or base';
}

/** May this crate hand out the side's pooled stocks (grenades, rockets)? Any crate; a FOB only while linked. */
export function servesPool(state: GameState, s: StructureState): boolean {
  return s.type !== 'fob' || isLinked(state, s);
}

/**
 * 69.4: every linked FOB's crate gains a bandage and a magazine, up to its stock. The Match calls it every FOB.refillS
 * seconds. Null when nothing changed. 69.4 step 5: not a side whose FOBs convoys fill (core/convoys.ts `linkRefills`).
 */
export function refillFobCrates(state: GameState): StructureState[] | null {
  let out: StructureState[] | null = null;
  state.structures.forEach((s, i) => {
    if (s.type !== 'fob' || s.health <= 0 || !s.supply || !isLinked(state, s)) return;
    const team = structureTeam(state, s);
    if (team !== null && !linkRefills(state, team)) return;   // 69.4 step 5: its convoys fill it
    const bandages = Math.min(FOB.stock.bandages, s.supply.bandages + 1), magazines = Math.min(FOB.stock.magazines, s.supply.magazines + 1);
    if (bandages === s.supply.bandages && magazines === s.supply.magazines) return;
    (out ??= state.structures.slice())[i] = { ...s, supply: { ...s.supply, bandages, magazines } };
  });
  return out;
}
